import {
  getRandomAtlasResponseUrl,
  getAnyAtlasClipUrl,
  type AtlasResponseCategory,
} from "./atlasVoiceManifest";

/**
 * Fire-and-forget ATLAS spoken response.
 *
 * GLOBAL by design: playback lives on a single module-level <audio> element
 * that is NOT tied to any component's lifecycle, so the clip keeps playing
 * through a route navigation that unmounts the voice card.
 *
 * Additive audio only: never throws, never awaits, never blocks the caller.
 * - Missing / empty category → silent no-op.
 * - Blocked autoplay, decode error, network error → swallowed.
 * - Only one clip plays at a time (a new trigger stops the previous one).
 * - No eager preloading: the clip loads only when actually triggered, so it
 *   never competes with the record → transcribe → parse request path.
 */
let el: HTMLAudioElement | null = null;

/** The long-lived global element (created lazily, reused for every clip). */
export function getAtlasResponseAudio(): HTMLAudioElement | null {
  if (typeof window === "undefined") return null;
  if (!el) {
    el = new Audio();
    el.preload = "none";
  }
  return el;
}

/**
 * Prime the element on a real user gesture so a later play() (which happens
 * after async work, far from the tap) is not blocked by autoplay policy.
 * Called by the app-level audio unlock installer.
 */
export async function primeAtlasResponseAudio(): Promise<boolean> {
  const a = getAtlasResponseAudio();
  const url = getAnyAtlasClipUrl();
  if (!a || !url) return false;
  try {
    if (!a.src) a.src = url;
    a.muted = true;
    const p = a.play();
    if (p && typeof p.then === "function") await p;
    try {
      a.pause();
      a.currentTime = 0;
    } catch {
      /* noop */
    }
    a.muted = false;
    return true;
  } catch {
    a.muted = false;
    return false;
  }
}

export function stopAtlasResponse(): void {
  try {
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
  } catch {
    /* silent */
  }
}

export function playAtlasResponse(category: AtlasResponseCategory): void {
  try {
    const url = getRandomAtlasResponseUrl(category);
    if (!url) {
      console.debug("[atlas-voice] no clips for category", category); // TEMP DIAGNOSTIC
      return;
    }
    const a = getAtlasResponseAudio();
    if (!a) return;
    // No-overlap: stop whatever is playing, then swap the source.
    try {
      a.pause();
    } catch {
      /* noop */
    }
    a.muted = false;
    a.src = url;
    a.currentTime = 0;
    console.debug("[atlas-voice] play", category, url); // TEMP DIAGNOSTIC
    void a
      .play()
      .then(() => console.debug("[atlas-voice] playing", category)) // TEMP DIAGNOSTIC
      .catch((err) => {
        // TEMP DIAGNOSTIC — distinguishes autoplay block from load failure.
        console.warn("[atlas-voice] play rejected", category, (err as Error)?.name ?? err);
      });
  } catch {
    /* silent — audio must never break posting */
  }
}
